import { makeAutoObservable, runInAction } from "mobx";

import {
  createQuestion,
  createSurveyForm,
  defaultOptionsForType,
  mapSurveyToForm,
} from "../utils/survey-form";

class SurveyFormStore {
  mode = "standard";
  surveyId = null;
  form = createSurveyForm();
  loadingSurvey = false;
  submitting = false;
  surveyStatus = "";

  questionDialogOpen = false;
  questionDraft = null;
  editingQuestionIndex = null;
  questionToDelete = null;
  companySearch = "";
  libraryOpen = false;

  constructor() {
    makeAutoObservable(this);
  }

  get isEdit() {
    return Boolean(this.surveyId);
  }

  get isStandard() {
    return this.mode === "standard";
  }

  get isPublished() {
    return this.surveyStatus === "published";
  }

  setContext = ({ mode, surveyId, defaultCategory }) => {
    this.mode = mode;
    this.surveyId = surveyId || null;
    this.resetForm(defaultCategory);
  };

  resetForm = (defaultCategory) => {
    this.form = createSurveyForm(defaultCategory);
    this.loadingSurvey = false;
    this.submitting = false;
    this.surveyStatus = "";
    this.questionDialogOpen = false;
    this.questionDraft = null;
    this.editingQuestionIndex = null;
    this.questionToDelete = null;
    this.companySearch = "";
    this.libraryOpen = false;
  };

  ensureCategory = (categories, defaultCategory) => {
    const nextCategory = defaultCategory || "common";
    if (!this.form.category || !categories.includes(this.form.category)) {
      this.form = { ...this.form, category: nextCategory };
    }
  };

  setLoadingSurvey = (value) => {
    this.loadingSurvey = value;
  };

  setSubmitting = (value) => {
    this.submitting = value;
  };

  setSurveyStatus = (status) => {
    this.surveyStatus = status || "";
  };

  setForm = (nextForm) => {
    this.form = nextForm;
  };

  updateField = (key, value) => {
    this.form = { ...this.form, [key]: value };
  };

  setCompanySearch = (value) => {
    this.companySearch = value;
  };

  setLibraryOpen = (value) => {
    this.libraryOpen = value;
  };

  setQuestionDialogOpen = (value) => {
    this.questionDialogOpen = value;
    if (!value) {
      this.editingQuestionIndex = null;
    }
  };

  openQuestionDialog = ({ index = null, defaultCategory }) => {
    if (index !== null && this.form.questions[index]) {
      this.questionDraft = this.form.questions[index];
      this.editingQuestionIndex = index;
    } else {
      const baseQuestion = createQuestion(
        defaultCategory,
        this.isStandard
          ? {}
          : {
              requirePoints: false,
              points: 0,
            },
      );
      this.questionDraft = {
        ...baseQuestion,
        options: defaultOptionsForType("text"),
      };
      this.editingQuestionIndex = null;
    }
    this.questionDialogOpen = true;
  };

  saveQuestion = (question) => {
    const nextQuestions = [...this.form.questions];
    if (this.editingQuestionIndex !== null) {
      nextQuestions[this.editingQuestionIndex] = question;
    } else {
      nextQuestions.push(question);
    }
    this.form = { ...this.form, questions: nextQuestions };
    this.questionDialogOpen = false;
    this.editingQuestionIndex = null;
  };

  setQuestionToDelete = (index) => {
    this.questionToDelete = index;
  };

  deleteQuestion = () => {
    if (this.questionToDelete === null) return;
    const nextQuestions = this.form.questions.filter(
      (_, idx) => idx !== this.questionToDelete,
    );
    this.form = { ...this.form, questions: nextQuestions };
    this.questionToDelete = null;
  };

  applyLoadedSurvey = (survey, defaultCategory) => {
    runInAction(() => {
      this.surveyStatus = survey?.status || "";
      this.form = mapSurveyToForm(survey, defaultCategory);
    });
  };
}

export const surveyFormStore = new SurveyFormStore();
